import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import decodeHTMLEntities from '@fogia/core-web/utils/decodeHTMLEntities';
import Link from '@fogia/core-web/components/Link';
import styled from 'libs/styled';
import Text from 'components/Text';

interface BreadcrumbItem {
    slug: string | null;
    title: string;
}

interface BreadcrumbsProps {
    breadcrumbs?: BreadcrumbItem[];
    [key: string]: unknown;
}

const Wrapper = styled('div')`
    margin-top: var(--header-height);
    padding: 3px 10px;
`;

const StyledBreadcrumbs = styled('div')`
    display: flex;
    align-items: center;
    gap: 5px;
    overflow: auto hidden;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const Separator = styled('span')`
    color: var(--opacity-primary-15);
    white-space: nowrap;
`;

const CurrentItem = styled('span')`
    color: var(--button-tertiery-text-disabled);
    white-space: nowrap;
`;

const LinkItem = styled(Link)`
    white-space: nowrap;
    color: var(--button-tertiery-text);
`;

interface ContentPageData {
    breadcrumbs?: { slug: string; title: string }[];
    title?: string;
}

interface ProductPageData {
    product?: { name: string };
}

interface SearchPageData {
    title?: string;
}

const useBreadcrumbs = (): BreadcrumbItem[] => {
    const { t } = useTranslation();
    const pageType = useAppSelector(({ page }) => page?.type);
    const pageData = useAppSelector(({ page }) => page?.data, shallowEqual);

    if (pageType === 'contentPage' || pageType === 'categoryPage') {
        const { breadcrumbs, title } = (pageData as ContentPageData) ?? {};
        const ancestorCrumbs = Array.isArray(breadcrumbs)
            ? breadcrumbs.map((breadcrumb) => ({
                  slug: breadcrumb.slug,
                  title: breadcrumb.title,
              }))
            : [];
        if (title) {
            return [...ancestorCrumbs, { slug: null, title }];
        }
        return ancestorCrumbs;
    }

    if (pageType === 'product') {
        const { product } = (pageData as ProductPageData) ?? {};
        return [
            { slug: '/products', title: t('breadcrumbs.all_products') },
            { slug: null, title: product?.name || '' },
        ];
    }

    if (pageType === 'searchPage') {
        const { title } = (pageData as SearchPageData) ?? {};
        return [{ slug: null, title: title ?? '' }];
    }

    return [];
};

const Breadcrumbs = ({ breadcrumbs: breadcrumbsProp, ...rest }: BreadcrumbsProps) => {
    const { t } = useTranslation();
    const hookBreadcrumbs = useBreadcrumbs();
    const breadcrumbs = breadcrumbsProp ?? hookBreadcrumbs;

    if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        return null;
    }

    const breadCrumbsWithStartPage: BreadcrumbItem[] = [{ slug: '/', title: t('breadcrumbs.home') }, ...breadcrumbs];

    return (
        <Wrapper {...rest} data-theme="light">
            <StyledBreadcrumbs>
                {breadCrumbsWithStartPage.map(({ slug, title }, index) => {
                    const key = `breadcrumb-${slug}-${index}`;
                    const isLast = index + 1 === breadCrumbsWithStartPage.length;
                    return (
                        <Fragment key={key}>
                            {index > 0 && (
                                <Separator>
                                    <Text as="span" typography="Primary/12_120_510_-1">
                                        /
                                    </Text>
                                </Separator>
                            )}
                            {isLast ? (
                                <CurrentItem>
                                    <Text as="span" typography="Primary/12_120_510_-1">
                                        {decodeHTMLEntities(title)}
                                    </Text>
                                </CurrentItem>
                            ) : (
                                <LinkItem to={slug}>
                                    <Text as="span" typography="Primary/12_120_510_-1">
                                        {decodeHTMLEntities(title)}
                                    </Text>
                                </LinkItem>
                            )}
                        </Fragment>
                    );
                })}
            </StyledBreadcrumbs>
        </Wrapper>
    );
};

export default Breadcrumbs;
